import {useState, useCallback, useEffect, useRef, memo} from 'react'
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {RadioButton} from "@/customComponents/uiComponents/RadioButton.jsx";
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import {blackSquareLogo} from '@img/index.js'
import {paymentService} from '@/services/paymentService'
import {parseCard} from '@/utils/functions'
import {useAppConfig} from '@/hooks/useAppConfig'
import {useUser} from '@/hooks/useUser'
import {resolveSquare} from '@/utils/square'
import {NoPaymentToday} from "@/customComponents/BookRideSteps/modalPages/paymentCards.jsx";

const MAX_RETRIES = 3

const CardsList = memo(function CardsList({cards, selectedId, onSelect, disabled = false}) {
    if (cards.length === 0) return <p className="text-(--text-secondary) text-sm mb-0!">No saved cards</p>

    return (
        <div className="flex flex-wrap gap-2 mt-3">
            {cards.map(card => {
                const {brand, last4} = parseCard(card)
                const isSelected = String(selectedId) === String(card.id)
                return (
                    <label key={card.id}
                           className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm transition-colors ${isSelected ? 'bg-(--accent-green-light)' : 'border border-[#ccc]'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                        <input type="radio" name="payment-card" value={card.id} checked={isSelected} disabled={disabled} onChange={() => { if (!disabled) onSelect(card.id) }} hidden/>
                        {brand || 'Card'} **** {last4 || '****'}
                    </label>
                )
            })}
        </div>
    )
})

/*
 * `closeOnAdded={false}` for a parent that moves on itself when a card is added — otherwise
 * onClose would also fire and take that parent's whole flow down with it.
 */
export default function AddCardPopup({isOpen, onClose, onCardAdded, showSetDefault = false, closeOnAdded = true}) {
    const [squareLoading, setSquareLoading] = useState(false)
    const [cardReady, setCardReady] = useState(false)
    const [isAddingCard, setIsAddingCard] = useState(false)
    const [cardError, setCardError] = useState('')
    const [setAsDefault, setSetAsDefault] = useState(false)

    const squareCardRef = useRef(null)
    const squareRetryRef = useRef(0)
    const squareCancelledRef = useRef(false)
    const squareTokenizedRef = useRef(false)

    // Per-user Square credentials: /config/app has the sandbox+production id
    // sets; GET /account's is_test_account picks which (refetched on auth change).
    const {data: appConfig} = useAppConfig()
    const {data: account} = useUser()
    const {appId: squareAppId, locationId: squareLocationId, sdkSrc: squareSdkSrc, status: squareStatus} = resolveSquare(appConfig?.square, account?.is_test_account)

    const nukeSquare = useCallback(() => {
        if (squareCardRef.current) {
            try { squareCardRef.current.destroy() } catch {}
            squareCardRef.current = null
        }
        document.querySelectorAll('script[src*="squarecdn.com"]').forEach(s => s.remove())
        delete window.Square
        const container = document.getElementById('hopn-add-card-container')
        if (container) container.innerHTML = ''
    }, [])

    const loadSquareScript = useCallback((sdkSrc) => {
        return new Promise((resolve, reject) => {
            if (window.Square) { resolve(); return }
            const script = document.createElement('script')
            script.src = sdkSrc
            script.async = true
            script.onload = resolve
            script.onerror = () => reject(new Error('Script load failed'))
            document.body.appendChild(script)
        })
    }, [])

    const initSquare = useCallback(async (isRetry = false) => {
        setSquareLoading(true)
        setCardReady(false)
        if (!isRetry) { setCardError(''); squareRetryRef.current = 0 }

        if (isRetry || squareTokenizedRef.current) {
            nukeSquare()
            squareTokenizedRef.current = false
        }

        if (!isRetry && squareCardRef.current) {
            try { squareCardRef.current.destroy() } catch {}
            squareCardRef.current = null
            const container = document.getElementById('hopn-add-card-container')
            if (container) container.innerHTML = ''
        }

        // Creds depend on /config/app (id sets) + is_test_account (GET /account).
        // While is_test_account isn't known yet keep loading (never guess an
        // environment); show "unavailable" only once known but the id set is missing.
        if (squareStatus === 'pending') { setSquareLoading(true); return }
        if (squareStatus !== 'ready') {
            setSquareLoading(false)
            setCardError('Card payment is temporarily unavailable. Please try again later.')
            return
        }

        try {
            if (!window.Square) await loadSquareScript(squareSdkSrc)
            if (!window.Square) throw new Error('Square not available')
            if (squareCancelledRef.current) return

            const payments = window.Square.payments(squareAppId, squareLocationId)
            // Hardcode a light theme so the card NEVER follows the browser's
            // prefers-color-scheme — Square's SDK renders its dark theme otherwise.
            const cardInstance = await payments.card({
                style: {
                    input: {color: '#2b2b2b', backgroundColor: '#ffffff'},
                    'input::placeholder': {color: '#8d8e8f'},
                    '.input-container': {borderColor: '#cfcdcd', borderRadius: '8px'},
                    '.input-container.is-focus': {borderColor: '#2b2b2b'},
                    '.input-container.is-error': {borderColor: '#d0021b'},
                    '.message-text': {color: '#8d8e8f'},
                    '.message-icon': {color: '#8d8e8f'},
                    '.message-text.is-error': {color: '#d0021b'},
                    '.message-icon.is-error': {color: '#d0021b'},
                },
            })
            if (squareCancelledRef.current) { cardInstance.destroy(); return }

            await cardInstance.attach('#hopn-add-card-container')
            squareCardRef.current = cardInstance
            squareRetryRef.current = 0
            setSquareLoading(false)
            setCardReady(true)
        } catch (err) {
            console.error('Square init failed:', err)
            if (squareCancelledRef.current) return
            nukeSquare()

            if (squareRetryRef.current < MAX_RETRIES) {
                squareRetryRef.current += 1
                await new Promise(r => setTimeout(r, squareRetryRef.current * 1500))
                if (!squareCancelledRef.current) return initSquare(true)
            }
            setSquareLoading(false)
            setCardError('Failed to load payment form. Please close and try again.')
        }
    }, [loadSquareScript, nukeSquare, squareAppId, squareLocationId, squareSdkSrc, squareStatus])

    useEffect(() => {
        if (!isOpen) return
        squareCancelledRef.current = false
        initSquare()
        return () => {
            squareCancelledRef.current = true
            if (squareCardRef.current) {
                try { squareCardRef.current.destroy() } catch {}
                squareCardRef.current = null
            }
        }
    }, [isOpen, squareAppId, squareStatus])

    const handleAddCard = useCallback(async () => {
        if (!squareCardRef.current) return
        setIsAddingCard(true)
        setCardError('')

        try {
            const result = await squareCardRef.current.tokenize()
            squareTokenizedRef.current = true

            if (result.status === 'OK') {
                const firstName = sessionStorage.getItem('hopnFirstName') || ''
                const lastName = sessionStorage.getItem('hopnLastName') || ''
                const fullName = `${firstName} ${lastName}`.trim() || 'Cardholder'

                const saveResult = await paymentService.storeCard({
                    squareToken: result.token,
                    displayName: fullName,
                    isDefault: setAsDefault,
                    metadata: result.details,
                })

                const savedCard = saveResult.data?.payment_method || saveResult.data
                if (saveResult.success && savedCard?.id) {
                    if (saveResult.replacedIds?.length > 0) {
                        setCardError('This card is already saved on your account. We have refreshed it for you.')
                        return
                    }
                    onCardAdded?.(savedCard)
                    if (closeOnAdded) onClose?.()
                } else {
                    setCardError(saveResult.error || 'Failed to save card. Please try again.')
                }
            } else {
                setCardError(result.errors?.[0]?.message || 'Unable to verify card details.')
            }
        } catch (err) {
            console.error('Card tokenization failed:', err)
            setCardError('Failed to process card. Please try again.')
        } finally {
            setIsAddingCard(false)
        }
    }, [onCardAdded, onClose, closeOnAdded, setAsDefault])

    const handleClose = useCallback(() => {
        setCardError('')
        setCardReady(false)
        setSetAsDefault(false)
        onClose?.()
    }, [onClose])

    return (
        <>
            <Modal isOpen={isOpen} onClose={handleClose} className="max-w-137.5!"
                   footer={
                       <button type="button" onClick={handleAddCard} disabled={!cardReady || isAddingCard}
                               className="hopn-add-passenger-button">
                           {squareLoading || isAddingCard ? <Loader color="#fff"/> : 'Add Card'}
                       </button>
                   }>
                <ModalHeader title="" onClose={handleClose}/>

                {/* Secure Payment — mirrors the booking-flow paymentCards step (only this section) */}
                <div className="hopn-secure-pay-head">
                    <h3 className="sub-title less-space payment-add-card-title">Secure Payment</h3>
                    <p className="hopn-powered-by">
                        Powered by
                        <img src={blackSquareLogo} alt="Square"/>
                        <strong>Square</strong>
                    </p>
                </div>

                {/* No payment today — the booking step's own block, imported rather than
                    re-typed so this popup can't drift from it. */}
                <NoPaymentToday/>

                {/* Credit or Debit Card */}
                <h6 className="hopn-cc-label">Credit or Debit Card</h6>
                <div className="payment-card-add-card payment-card-step">
                    <div id="wrapper">
                        {squareLoading && <div className="text-center py-4"><Loader/></div>}
                        <div id="hopn-add-card-container"></div>
                    </div>
                </div>

                {/* Set as default */}
                {showSetDefault && (
                    <label className="flex items-center gap-2 cursor-pointer mt-4 mb-4 select-none">
                        <RadioButton type="checkbox" checked={setAsDefault} onChange={(e) => setSetAsDefault(e.target.checked)}/>
                        <span className="text-sm">Set Default card</span>
                    </label>
                )}

                {cardError && <p className="text-red-500 text-sm mb-3">{cardError}</p>}
            </Modal>
        </>
    )
}

export {CardsList}
